import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import GalapagosExperienceMap from '../components/registry/GalapagosExperienceMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ZOLA_REGISTRY_URL = 'https://www.zola.com/registry/tillgraesslewedding';
const GALAPAGOS_EXPERIENCES = [
  {
    id: 'santa-cruz-tortoises',
    number: '01',
    island: 'Santa Cruz',
    title: 'Highlands Giant Tortoise Walk',
    duration: 'Half day',
    price: '$120',
    coords: [-0.6772, -90.4422],
    image: '/images/galapagos/santa-cruz-tortoise.jpg',
    imageSrcSet: '/images/galapagos/santa-cruz-tortoise-800.webp 800w, /images/galapagos/santa-cruz-tortoise-1200.webp 1200w',
    imageAlt: 'A Santa Cruz giant tortoise resting in the highland grass',
    zolaItemId: '6a624597f17a511822d08700',
    zolaUrl: `${ZOLA_REGISTRY_URL}?collectionItemId=6a624597f17a511822d08700`,
    description:
      'A walk through a highland reserve where giant tortoises roam freely, with time to explore the area’s lava tunnels.',
  },
  {
    id: 'bartolome-cruise',
    number: '02',
    island: 'Bartolomé',
    title: 'Bartolomé Island Day Cruise',
    duration: 'Full day',
    price: '$710',
    coords: [-0.2839, -90.5516],
    image: '/images/galapagos/bartolome-pinnacle-rock.jpg',
    imageSrcSet: '/images/galapagos/bartolome-pinnacle-rock-800.webp 800w, /images/galapagos/bartolome-pinnacle-rock-1200.webp 1200w',
    imageAlt: 'Pinnacle Rock and the volcanic landscape of Bartolomé Island',
    zolaItemId: '6a6245b49251e6adec2aa7fc',
    zolaUrl: `${ZOLA_REGISTRY_URL}?collectionItemId=6a6245b49251e6adec2aa7fc`,
    description:
      'A boat trip from Santa Cruz for the overlook above Pinnacle Rock, a volcanic landscape walk, and snorkeling.',
  },
  {
    id: 'los-tuneles',
    number: '03',
    island: 'Isabela',
    title: 'Los Túneles Snorkeling',
    duration: 'About 5 hours',
    price: '$280',
    coords: [-1.051, -91.1703],
    image: '/images/galapagos/los-tuneles-sea-lion.jpg',
    imageSrcSet: '/images/galapagos/los-tuneles-sea-lion-800.webp 800w, /images/galapagos/los-tuneles-sea-lion-1200.webp 1200w',
    imageAlt: 'A Galápagos sea lion at Los Túneles on Isabela Island',
    zolaItemId: '6a6245ba9251e6adec2aa803',
    zolaUrl: `${ZOLA_REGISTRY_URL}?collectionItemId=6a6245ba9251e6adec2aa803`,
    description:
      'A small-boat trip through lava arches and sheltered water, followed by snorkeling for marine turtles, rays, sea lions, and seahorses.',
  },
  {
    id: 'sierra-negra',
    number: '04',
    island: 'Isabela',
    title: 'Sierra Negra Volcano Hike',
    duration: 'About 5 hours',
    price: '$100',
    coords: [-0.83, -91.17],
    image: '/images/galapagos/sierra-negra.jpg',
    imageSrcSet: '/images/galapagos/sierra-negra-800.webp 800w, /images/galapagos/sierra-negra-1200.webp 1200w',
    imageAlt: 'Volcanic terrain on the slopes of Sierra Negra',
    zolaItemId: '6a6245c0ca9e70e5a9e4acf2',
    zolaUrl: `${ZOLA_REGISTRY_URL}?collectionItemId=6a6245c0ca9e70e5a9e4acf2`,
    description:
      'A guided 15-kilometer hike to the rim of Sierra Negra’s broad caldera and across the lava fields of Volcán Chico.',
  },
  {
    id: 'kicker-rock',
    number: '05',
    island: 'San Cristóbal',
    title: 'Kicker Rock Snorkeling',
    duration: '6–7 hours',
    price: '$340',
    coords: [-0.7771, -89.5199],
    image: '/images/galapagos/kicker-rock.jpg',
    imageSrcSet: '/images/galapagos/kicker-rock-800.webp 800w, /images/galapagos/kicker-rock-1024.webp 1024w',
    imageAlt: 'Kicker Rock rising from the water near San Cristóbal Island',
    zolaItemId: '6a6245c6e087d107ddc497b6',
    zolaUrl: `${ZOLA_REGISTRY_URL}?collectionItemId=6a6245c6e087d107ddc497b6`,
    description:
      'A day on the water around the sea stack known locally as León Dormido, with snorkeling for turtles, rays, and Galápagos sharks.',
  },
];
const GALAPAGOS_ZOLA_FUND_IDS = new Set(
  GALAPAGOS_EXPERIENCES.map((experience) => experience.zolaItemId),
);

function EmbeddedZolaRegistry({
  itemLimit = null,
  showFullRegistryLink = false,
  onLoadComplete = null,
}) {
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const onLoadCompleteRef = useRef(onLoadComplete);
  const embeddedItems = (registry?.items || []).filter(
    (item) => !GALAPAGOS_ZOLA_FUND_IDS.has(item.id),
  );
  const visibleItems =
    Number.isInteger(itemLimit) && itemLimit > 0
      ? embeddedItems.slice(0, itemLimit)
      : embeddedItems;
  const hasMoreItems = visibleItems.length < embeddedItems.length;

  useEffect(() => {
    onLoadCompleteRef.current = onLoadComplete;
  }, [onLoadComplete]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadRegistry() {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch(`${BACKEND_URL}/api/registry/zola`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Registry sync failed');
        }
        setRegistry(await response.json());
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          onLoadCompleteRef.current?.();
        }
      }
    }

    loadRegistry();
    return () => controller.abort();
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="zola-sync-state" aria-live="polite">
        <span className="zola-sync-state__mark" aria-hidden="true">&amp;</span>
        <p>Loading our registry…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="zola-sync-state" role="status">
        <span className="zola-sync-state__mark" aria-hidden="true">&amp;</span>
        <h3>The registry is still syncing.</h3>
        <p>Please try again in a moment, or open our registry directly on Zola.</p>
        <button type="button" onClick={() => setReloadKey((key) => key + 1)}>
          Try again
        </button>
        <a
          href={ZOLA_REGISTRY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="zola-sync-state__link"
        >
          Open registry on Zola
          <ArrowUpRight size={15} aria-hidden="true" />
        </a>
      </div>
    );
  }

  return (
    <div className="zola-synced-registry">
      <div className="zola-synced-registry__intro">
        <p>
          {hasMoreItems
            ? 'Here are a few gifts from our registry. Purchases are completed securely on Zola.'
            : 'Browse our complete collection of wedding gifts. Purchases are completed securely on Zola.'}
        </p>
        <span>
          {hasMoreItems
            ? `Showing ${visibleItems.length} of ${embeddedItems.length} gifts`
            : `${embeddedItems.length} ${embeddedItems.length === 1 ? 'gift' : 'gifts'}`}
        </span>
      </div>

      {embeddedItems.length ? (
        <>
          <div className="zola-gift-grid">
            {visibleItems.map((item) => (
              <article className="zola-gift-card" key={item.id}>
                <div className="zola-gift-card__image">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span aria-hidden="true">&amp;</span>
                  )}
                  {item.fulfilled && <em>Purchased</em>}
                </div>
                <div className="zola-gift-card__content">
                  <p>{item.brand}</p>
                  <h3>{item.name}</h3>
                  <strong>{item.price_label}</strong>
                  {item.cash_fund && item.percent_complete > 0 && (
                    <div
                      className="zola-gift-card__progress"
                      aria-label={`${item.percent_complete}% funded`}
                    >
                      <span style={{ width: `${item.percent_complete}%` }} />
                    </div>
                  )}
                  <a
                    href={item.url || ZOLA_REGISTRY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${item.button_label}: ${item.name}`}
                  >
                    {item.button_label}
                    <ArrowUpRight size={15} aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}

            {showFullRegistryLink && hasMoreItems && (
              <Link
                to="/registry/gifts"
                className="registry-view-all-card"
                aria-label={`View all ${embeddedItems.length} registry gifts`}
              >
                <span className="registry-view-all-card__eyebrow">
                  Complete Registry
                </span>
                <span className="registry-view-all-card__content">
                  <strong>View All Gifts</strong>
                  <span>{embeddedItems.length} gifts in our collection</span>
                </span>
                <span className="registry-view-all-card__arrow" aria-hidden="true">
                  <ArrowRight size={22} />
                </span>
              </Link>
            )}
          </div>
        </>
      ) : (
        <div className="zola-sync-state">
          <span className="zola-sync-state__mark" aria-hidden="true">&amp;</span>
          <h3>Our registry is on its way.</h3>
          <p>New gifts will appear here as we add them.</p>
        </div>
      )}
    </div>
  );
}

export function RegistryGiftsPage() {
  return (
    <div className="registry-page" data-testid="registry-gifts-page">
      <header className="page-hero">
        <div className="page-hero__inner">
          <div>
            <p className="eyebrow text-olive mb-4">Wedding Registry</p>
            <h1 className="page-title">All Gifts</h1>
          </div>
          <p className="registry-hero__copy">
            Browse the complete collection of gifts we have chosen for our
            home.
          </p>
        </div>
      </header>

      <section className="registry-zola registry-zola--all">
        <div className="registry-zola__inner">
          <Link to="/registry" className="registry-gifts-back">
            <ArrowLeft size={16} aria-hidden="true" />
            Back to registry overview
          </Link>

          <div className="registry-zola__frame">
            <EmbeddedZolaRegistry />
          </div>
        </div>
      </section>
    </div>
  );
}

export default function RegistryPage() {
  const [registryPreviewLoaded, setRegistryPreviewLoaded] = useState(false);
  const [selectedExperienceId, setSelectedExperienceId] = useState(
    GALAPAGOS_EXPERIENCES[0].id,
  );
  const selectedExperience =
    GALAPAGOS_EXPERIENCES.find(
      (experience) => experience.id === selectedExperienceId,
    ) || GALAPAGOS_EXPERIENCES[0];

  useEffect(() => {
    if (!registryPreviewLoaded || window.location.hash !== '#galapagos') {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      document.getElementById('galapagos')?.scrollIntoView();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [registryPreviewLoaded]);

  return (
    <div className="registry-page" data-testid="registry-page">
      <header className="page-hero">
        <div className="page-hero__inner">
          <div>
            <p className="eyebrow text-olive mb-4">Wedding Registry</p>
            <h1 className="page-title">Registry</h1>
          </div>
          <p className="registry-hero__copy">
            Gifts for our home, along with experiences for our Galápagos
            honeymoon.
          </p>
        </div>
      </header>

      <section className="registry-zola registry-zola--preview">
        <div className="registry-zola__inner">
          <div className="registry-zola__frame">
            <EmbeddedZolaRegistry
              itemLimit={3}
              showFullRegistryLink
              onLoadComplete={() => setRegistryPreviewLoaded(true)}
            />
          </div>
        </div>
      </section>

      <section id="galapagos" className="registry-journey">
        <div className="registry-journey__inner">
          <div className="registry-journey__header">
            <div>
              <p className="eyebrow text-olive">After the Wedding</p>
              <h2>The Galápagos</h2>
            </div>
            <p>
              We are planning a trip through the Galápagos for our honeymoon.
              These are a few of the places and experiences we hope to include.
            </p>
          </div>

          <div className="registry-map-layout">
            <GalapagosExperienceMap
              experiences={GALAPAGOS_EXPERIENCES}
              selectedId={selectedExperienceId}
              onSelect={setSelectedExperienceId}
            />

            <article className="registry-experience">
              <figure className="registry-experience__image">
                <img
                  key={selectedExperience.image}
                  src={selectedExperience.image}
                  srcSet={selectedExperience.imageSrcSet}
                  sizes="(min-width: 1024px) 42vw, 100vw"
                  alt={selectedExperience.imageAlt}
                  loading="lazy"
                  decoding="async"
                />
                <span>{selectedExperience.number}</span>
              </figure>

              <div key={selectedExperience.id} className="registry-experience__content experience-transition">
                <div className="registry-experience__meta">
                  <span>{selectedExperience.island}</span>
                  <span>{selectedExperience.duration}</span>
                </div>
                <h3>{selectedExperience.title}</h3>
                <p>{selectedExperience.description}</p>
                <div className="registry-experience__gift">
                  <div>
                    <span>Estimated for two</span>
                    <strong>{selectedExperience.price}</strong>
                  </div>
                  <a
                    href={selectedExperience.zolaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Contribute
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </article>
          </div>

          <div className="registry-experience-picker" aria-label="Choose an experience">
            {GALAPAGOS_EXPERIENCES.map((experience) => (
              <button
                key={experience.id}
                type="button"
                className={
                  experience.id === selectedExperienceId ? 'is-active' : undefined
                }
                onClick={() => setSelectedExperienceId(experience.id)}
                aria-pressed={experience.id === selectedExperienceId}
              >
                <span>{experience.number}</span>
                <span>
                  <small>{experience.island}</small>
                  <strong>{experience.title}</strong>
                </span>
                <em>{experience.price}</em>
              </button>
            ))}
          </div>

        </div>
      </section>
    </div>
  );
}
